import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { fadeInUp, staggerContainer } from "@/motion/variants";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { TradingTrustStrip } from "@/components/TradingTrustStrip";
import { FeatureBadges } from "@/components/MicroTrustBadges";
import { TradingPartnersTrustBar } from "@/components/TrustBar";
import { 
  Package, 
  DollarSign, 
  Target, 
  Shield, 
  FileText, 
  Ship, 
  Building2, 
  FileCheck, 
  Banknote,
  ShoppingCart,
  Globe,
  Factory,
  CheckCircle,
  Award,
  Truck,
  Users,
  TrendingUp,
  Palette,
  Briefcase,
  BarChart3,
  Languages,
  UserCheck,
  Container,
  Download,
  Eye,
  ArrowRight
} from "lucide-react";

const ConsumerPack = () => {
  // Product categories with descriptions
  const productCategories = [
    {
      title: "Aceites y Grasas",
      icon: <Package className="h-6 w-6" />,
      items: [
        "Aceite vegetal para cocinar (palm-based, mezclas, CP10 / CP8, soja, etc.)",
        "Aceite para freír y compuestos (servicio de alimentos)",
        "Manteca y margarina (estables en almacén, panadería y mesa)",
        "... y más"
      ],
      image: "/consumer-pack/oil-fat.png"
    },
    {
      title: "Lácteos y Relacionados",
      icon: <Package className="h-6 w-6" />,
      items: [
        "Leche condensada / leche evaporada (incluyendo opciones con grasa vegetal)",
        "Sustitutos lácteos para panadería y confitería",
        "... y más"
      ],
      image: "/consumer-pack/dairy.jpg"
    },
    {
      title: "Básicos",
      icon: <Package className="h-6 w-6" />,
      items: [
        "Arroz (blanco / parbolizado; retail y bolsas de 25–50 kg)",
        "Harina de trigo (multiuso / panadería)",
        "Pasta (espagueti / macarrones; retail y packs para servicio de alimentos)",
        "... y más"
      ],
      image: "/consumer-pack/staples.jpg"
    },
    {
      title: "Cuidado del Hogar",
      icon: <Package className="h-6 w-6" />,
      items: [
        "Detergente en polvo (económico, alta espuma)",
        "Jabones para lavandería / multiuso",
        "... y más"
      ],
      image: "/consumer-pack/home.webp"
    },
    {
      title: "Alimentos Enlatados y Procesados",
      icon: <Package className="h-6 w-6" />,
      items: [
        "Sardinas / caballa en salsa",
        "Pasta de tomate (latas pequeñas y grandes / bolsas)",
        "Frijoles horneados y productos enlatados similares",
        "... y más"
      ],
      image: "/consumer-pack/canned.webp"
    }
  ];

  // Certification images from public/certifications folder
  const certifications = [
    "/certifications/certifications-icons-01.png",
    "/certifications/certifications-icons-02.png",
    "/certifications/certifications-icons-04.png",
    "/certifications/certifications-icons-05.png",
    "/certifications/certifications-icons-06.png",
    "/certifications/certifications-icons-09.png",
    "/certifications/certifications-icons-10.png",
    "/certifications/certifications-icons-10%20(1).png"
  ];

  // Brands showcase (same as /brands page, compact for this landing)
  const brandsPreview = [
    {
      name: "MIVIDA",
      tagline: "Plataforma de salud al consumidor liderada por médicos",
      logo: "/brands/mivida.png",
      color: "blue" as const
    },
    {
      name: "Salvadora",
      tagline: "Productos esenciales para mercados en desarrollo",
      logo: "/brands/salvadora.png",
      color: "gold" as const
    }
  ];

  const catalogues = [
    {
      title: "Catálogo de Aceites y Grasas",
      description: "Mejor para Panaderías y Restaurantes",
      file: "/catalogues/Padmora_OKI_Catalogo.pdf",
      icon: <Package className="h-6 w-6" />
    },
    {
      title: "Catálogo de Productos Lácteos",
      description: "Mejor para tus desayunos",
      file: "/catalogues/Padmora_LECHE_CATALOGO.pdf",
      icon: <Package className="h-6 w-6" />
    },
  ];

  return (
    <div className="min-h-screen pt-6">
      <SEO
        title="Productos de Consumo | Distribución Asia al Caribe | Padmora"
        description="Distribución directa de productos para consumo hacia mercados de América Central y el Caribe. Compramos EXW/FCA y vendemos CIF, eliminando intermediarios para entregar productos esenciales a precios competitivos."
        image="/og-consumer-pack.jpg"
        canonical="https://padmora.com/consumer-pack"
        keywords={[
          "distribución productos consumo",
          "Asia al Caribe",
          "productos ASEAN",
          "bienes de consumo",
          "aceite de cocina",
          "productos lácteos",
          "distribución de arroz",
          "productos de limpieza",
          "alimentos enlatados",
          "B2B servicio de alimentos",
          "distribución retail",
          "EXW FCA CIF",
          "abastecimiento directo fábrica"
        ]}
        aiOptimization={{
          aiKeywords: [
            "direct factory sourcing",
            "supply chain optimization",
            "consumer goods distribution",
            "ASEAN manufacturing",
            "Caribbean market access"
          ],
          entities: [
            "Consumer Pack",
            "ASEAN",
            "Caribbean",
            "EXW",
            "FCA",
            "CIF",
            "Mewah Group",
            "Wilmar International"
          ],
          topics: [
            "Consumer Goods Distribution",
            "Supply Chain Management",
            "International Trade",
            "Retail Distribution",
            "Food Service Distribution"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Distribución de Productos de Consumo",
          "description": "Distribución directa de productos para consumo hacia mercados de América Central y el Caribe, eliminando intermediarios para soluciones rentables.",
          "provider": {
            "@type": "Organization",
            "name": "Padmora",
            "url": "https://padmora.com"
          },
          "serviceType": "Distribución de Bienes de Consumo",
          "areaServed": [
            {
              "@type": "Place",
              "name": "Caribe"
            },
            {
              "@type": "Place",
              "name": "ASEAN"
            }
          ]
        }}
        breadcrumbs={[
          { name: "Inicio", url: "https://padmora.com/" },
          { name: "Productos de Consumo", url: "https://padmora.com/consumer-pack" }
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-slate-900 mb-6"
            >
              Distribución de Productos de Consumo
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-slate-600 mb-8"
            >
              Distribución directa de productos para consumo hacia mercados de América Central y el Caribe
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center">
              <Button
                size="lg"
                className="px-8 py-4 text-lg"
                asChild
              >
                <Link to="/store" className="inline-flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Visita nuestra tienda
                </Link>
              </Button>
              <Button
                size="lg"
                className="px-8 py-4 text-lg"
                variant="secondary"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Enviar consulta
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="px-8 py-4 text-lg"
                asChild
              >
                <Link to="/trading" className="text-slate-700 hover:text-slate-900">
                  Ver Servicios de Trading →
                </Link>
              </Button>
            </motion.div>
          </motion.div>

          <TradingTrustStrip
            locale="es"
            showBanking={false}
            additionalBadges={[
              {
                icon: <Factory className="h-4 w-4" />,
                label: "Abastecimiento Directo de Fábricas en Puntos de Origen",
                tooltip: "Eliminando intermediarios mediante abastecimiento directo de fabricantes",
              },
              {
                icon: <Globe className="h-4 w-4" />,
                label: "Experiencia en Mercado del Caribe",
                tooltip: "Comprensión profunda de las necesidades y preferencias del consumidor caribeño",
              },
            ]}
          />

          {/* Partners at bottom of hero */}
          <div className="mt-16">
            <TradingPartnersTrustBar />
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12 text-slate-900"
            >
              ¿Por Qué Elegir Distribución Directa?
            </motion.h2>
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-8 rounded-lg shadow-sm"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Target className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-slate-900">Entendemos el Caribe</h3>
                <p className="text-slate-600">
                  Conocimiento profundo de las preferencias del mercado caribeño, requisitos regulatorios y necesidades del consumidor. Adaptamos nuestra mezcla de productos para coincidir con la demanda local.
                </p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-8 rounded-lg shadow-sm"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-slate-900">Eliminamos Intermediarios</h3>
                <p className="text-slate-600">
                  El abastecimiento directo de fábricas ASEAN significa mejores precios, entrega más rápida y mayor control sobre la calidad y especificaciones del producto.
                </p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-8 rounded-lg shadow-sm"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Truck className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-slate-900">Logística Optimizada</h3>
                <p className="text-slate-600">
                  Compramos EXW o FCA y vendemos CIF, manejando toda la logística, seguros y flete para garantizar una entrega fluida a los puertos del Caribe.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-4 text-slate-900"
            >
              Productos Esenciales
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-center text-slate-600 mb-12"
            >
              Disponibles en varios tamaños para consumidores finales y clientes B2B (panaderías, restaurantes y servicio de alimentos)
            </motion.p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {productCategories.map((category, index) => (
                <motion.div
                  key={category.title}
                  variants={fadeInUp}
                  className="bg-white border border-border/50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      {category.icon}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">{category.title}</h3>
                  </div>
                  <div className="mb-4">
                    <img
                      src={category.image}
                      alt={category.title}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                  <ul className="space-y-2">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="text-slate-600 text-sm flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Catalogues Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-4 text-slate-900"
            >
              Catálogos de Productos
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-center text-slate-600 mb-12"
            >
              Explora nuestros catálogos completos de productos adaptados para diferentes segmentos de mercado
            </motion.p>
            <div className="grid md:grid-cols-3 gap-8">
              {catalogues.map((catalogue) => (
                <motion.div
                  key={catalogue.title}
                  variants={fadeInUp}
                  className="bg-white border border-border/50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      {catalogue.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900">{catalogue.title}</h3>
                    </div>
                  </div>
                  <p className="text-slate-600 mb-6 text-sm">
                    {catalogue.description}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="default"
                      className="flex-1"
                      asChild
                    >
                      <a
                        href={catalogue.file}
                        className="flex items-center justify-center gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        Ver
                      </a>
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      asChild
                    >
                      <a
                        href={catalogue.file}
                        download
                        className="flex items-center justify-center gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Descargar
                      </a>
                    </Button>
                  </div>
                </motion.div>
              ))}

              {/* B2B Store CTA card */}
              <motion.div
                variants={fadeInUp}
                className="bg-white border-2 border-primary/30 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <ShoppingCart className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900">Catálogo Completo B2B</h3>
                  </div>
                </div>
                <p className="text-slate-600 mb-6 text-sm flex-1">
                  Explora todo nuestro catálogo de productos al por mayor y solicita cotización directamente
                </p>
                <Button variant="default" className="w-full" asChild>
                  <Link to="/store" className="flex items-center justify-center gap-2">
                    Explorar Store B2B
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Nuestras marcas — link to /brands for full portfolio */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-4 text-slate-900"
            >
              Nuestras marcas
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-center text-slate-600 mb-12"
            >
              Marcas de consumo con foco en salud, bienestar y productos esenciales
            </motion.p>
            <div className="grid md:grid-cols-2 gap-8 mb-10">
              {brandsPreview.map((brand) => (
                <motion.div
                  key={brand.name}
                  variants={fadeInUp}
                  className={`bg-white border border-border/50 p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center ${
                    brand.color === "blue"
                      ? "bg-gradient-to-br from-white to-blue-50/30"
                      : "bg-gradient-to-br from-white to-amber-50/30"
                  }`}
                >
                  <div
                    className={`w-32 h-32 flex items-center justify-center rounded-xl p-4 border-2 mb-4 ${
                      brand.color === "blue" ? "bg-white border-blue-200" : "bg-white border-amber-200"
                    }`}
                  >
                    <img
                      src={brand.logo}
                      alt={`${brand.name} logo`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-1">{brand.name}</h3>
                  <p className="text-slate-600">{brand.tagline}</p>
                </motion.div>
              ))}
            </div>
            <motion.div variants={fadeInUp} className="text-center">
              <Button size="lg" variant="outline" className="px-6 py-3" asChild>
                <Link to="/brands" className="text-slate-700 hover:text-slate-900">
                  Ver todas nuestras marcas →
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Private Label & Brand Building Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.div
              variants={fadeInUp}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Marca Privada y Construcción de Marca
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Construye una posición de mercado defendible con una solución de marca completa bajo el paraguas de Padmora
              </p>
            </motion.div>

            {/* What You Get - Grid Layout */}
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Award className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">Tu Propia Marca de Mercado</h3>
                <p className="text-sm text-slate-600">Identidad de marca exclusiva para tu territorio</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Palette className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">Diseño Profesional</h3>
                <p className="text-sm text-slate-600">Embalaje listo para retail y sistema de marca</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">Gama de Productos Curada</h3>
                <p className="text-sm text-slate-600">Portafolio coherente construido para tu mercado</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Languages className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">Mensajería Localizada</h3>
                <p className="text-sm text-slate-600">Adaptado al idioma local y regulaciones</p>
              </motion.div>
            </motion.div>

            {/* Two Column Layout */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Full Brand Management */}
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-8 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <UserCheck className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Gestión Completa de Marca</h3>
                </div>
                <p className="text-slate-600 mb-4">
                  Padmora actúa como tu oficina de marca externalizada
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-1 flex-shrink-0" />
                    <span className="text-sm text-slate-700">Gestión de marca y portafolio</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-1 flex-shrink-0" />
                    <span className="text-sm text-slate-700">Coordinación de fábrica y calidad</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-1 flex-shrink-0" />
                    <span className="text-sm text-slate-700">Arte y cumplimiento</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-1 flex-shrink-0" />
                    <span className="text-sm text-slate-700">Gerente de marca dedicado</span>
                  </li>
                </ul>
                <p className="text-sm text-slate-600 italic mt-4">
                  Tú vendes. Nosotros gestionamos la marca.
                </p>
              </motion.div>

              {/* Performance-Based Eligibility */}
              <motion.div
                variants={fadeInUp}
                className="bg-blue-50 border border-blue-200 p-8 rounded-lg"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <BarChart3 className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Elegibilidad de Marca Propia</h3>
                </div>
                <p className="text-slate-700 mb-4">
                  Exclusividad más profunda para socios comprometidos
                </p>
                <div className="bg-white rounded-lg p-4 mb-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Container className="h-5 w-5 text-blue-600" />
                    <span className="font-bold text-slate-900">5+ contenedores por producto</span>
                  </div>
                  <p className="text-xs text-slate-600">Requisito mínimo de volumen recurrente</p>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-1 flex-shrink-0" />
                    <span className="text-sm text-slate-700">Acuerdo de marca a largo plazo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-1 flex-shrink-0" />
                    <span className="text-sm text-slate-700">Protección de territorio</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-1 flex-shrink-0" />
                    <span className="text-sm text-slate-700">Exclusividad basada en rendimiento</span>
                  </li>
                </ul>
              </motion.div>
            </div>

            {/* Ideal For - Compact */}
            <motion.div
              variants={fadeInUp}
              className="mb-12"
            >
              <h3 className="text-lg font-bold text-slate-900 mb-6 text-center">Ideal para distribuidores que buscan</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <FeatureBadges
                  features={[
                    { label: "Solución de marca lista para escalar" }
                  ]}
                  align="center"
                  variant="features"
                  className="md:justify-start"
                />
                <FeatureBadges
                  features={[
                    { label: "Sensación de marca propia, cero carga operativa" }
                  ]}
                  align="center"
                  variant="features"
                  className="md:justify-start"
                />
                <FeatureBadges
                  features={[
                    { label: "Camino de crecimiento hacia exclusividad más fuerte" }
                  ]}
                  align="center"
                  variant="features"
                  className="md:justify-start"
                />
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              variants={fadeInUp}
              className="text-center"
            >
              <p className="text-slate-700 mb-6 max-w-2xl mx-auto">
                ¿Construyendo o expandiendo tu portafolio? Nosotros diseñamos y gestionamos la marca mientras tú lideras la ejecución local.
              </p>
              <Button
                size="lg"
                className="px-8 py-4 text-lg bg-primary hover:bg-primary/90 text-white"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Enviar consulta
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Mechanism Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-8 text-slate-900"
            >
              Nuestro Mecanismo de Distribución
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-center text-slate-600 mb-12"
            >
              El mismo mecanismo probado de nuestros servicios de trading, optimizado para productos de consumo envasados
            </motion.p>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-3 gap-8"
            >
              <motion.div
                variants={fadeInUp}
                className="text-center bg-card/50 border border-border/50 p-6 rounded-lg"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Factory className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3">Origen (EXW/FCA)</h3>
                <p className="text-slate-600 text-sm">
                  Compra directa de fábricas ASEAN en términos Ex Works o Free Carrier, garantizando control de calidad en origen.
                </p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="text-center bg-card/50 border border-border/50 p-6 rounded-lg"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Ship className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3">Logística y Flete</h3>
                <p className="text-slate-600 text-sm">
                  Gestionamos todo el envío, seguros y documentación para garantizar que los productos lleguen de forma segura y a tiempo.
                </p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="text-center bg-card/50 border border-border/50 p-6 rounded-lg"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <ShoppingCart className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3">Entrega (CIF)</h3>
                <p className="text-slate-600 text-sm">
                  Productos entregados Costo, Seguro y Flete a puertos del Caribe, listos para distribución local.
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-4 text-slate-900"
            >
              Certificaciones de Calidad y Seguridad
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-center text-slate-600 mb-8 max-w-3xl mx-auto"
            >
              La calidad y seguridad del producto es una prioridad clave en el proceso de producción de nuestros proveedores. Desplegamos equipos de medición y laboratorio actualizados que garantizan un monitoreo eficiente de indicadores fisicoquímicos y de seguridad en cada etapa de fabricación. La calidad y seguridad de nuestros productos están respaldadas por documentos oficiales de garantía de calidad, certificados de cumplimiento con los requisitos de documentos regulatorios y validados adicionalmente con reconocimientos y diplomas.
            </motion.p>
            <motion.div
              variants={staggerContainer}
              className="grid grid-cols-2 md:grid-cols-4 gap-6"
            >
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white border border-border/50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow flex items-center justify-center"
                >
                  <img
                    src={cert}
                    alt={`Certificación ${index + 1}`}
                    className="w-full h-auto object-contain max-h-32"
                    loading="lazy"
                  />
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-slate-900 mb-6"
            >
              ¿Listo para Abastecerte de Productos de Consumo?
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 mb-8"
            >
              Conéctate con nosotros para discutir tus necesidades de productos de consumo y explora cómo podemos ayudarte a abastecerte directamente de fábricas ASEAN.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button
                size="lg"
                variant="default"
                className="px-8 py-4 text-lg bg-primary hover:bg-primary/90 text-white"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Enviar consulta
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ConsumerPack;

